import { authenticationStore } from "@/state/auth/AuthStore";
import { AuthenticationContext } from "@/type/core/auth/auth.types";
import { Signature } from "@/type/core/dashboard/dashboard.types";
import { Picture } from "@/type/core/offices/offices.types";
import { TransactionType } from "@/type/core/transaction/transaction.types";
import configuration from "@/utils/Config";
import { SignaturePicturePriorityEnum } from "@/utils/enums/signature.picture.priority.enum";
import EnumHelper from "@/utils/helpers/enum-helper";
import SignatureHelper from "@/utils/helpers/signature-helper";
import useOfficesSignaturePicturesDefault from "@/utils/hooks/useOfficesSignaturePicturesDefault";
import { getToken } from "@/utils/rest/RestClient";
import { useUnit } from "effector-react";

interface UseSignatoryCardPictureReturn {
  signatoryDefaultPicture?: Picture;
  signatorySignaturePicturesWithCorrectUrl: Picture[];
  sealingSignaturePicturesWithCorrectUrl: Picture[];
  officeSignaturePictures: Picture[];
  officeSealingPictures: Picture[];
  sealingDefaultPicture?: Picture;
  sealingDefaultPriorityRule?: SignaturePicturePriorityEnum;
  signature?: Signature;
  src?: string;
}

const useSignatoryCardPicture = (
  officeId: string,
  isUserInOffice: boolean = false,
  signatures: Signature[],
  signatorySignaturePictures: Picture[] = [],
  sealingSignaturePictures: Picture[] = [],
  documentRank: number,
  email: string,
  transactionType: TransactionType,
  name?: string,
  signatureIdentificator?: string,
): UseSignatoryCardPictureReturn => {
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);

  const officeSignaturePicturesDefaultHook =
    useOfficesSignaturePicturesDefault(officeId);
  const officeSignaturePictures = isUserInOffice
    ? officeSignaturePicturesDefaultHook.officeSignaturePictures
    : [];
  const officeSealingPictures = isUserInOffice
    ? officeSignaturePicturesDefaultHook.officeSealingPictures
    : [];

  const signatorySignaturePicturesWithCorrectUrl =
    signatorySignaturePictures.map((picture) => {
      const url = `${configuration.backendBaseUrl}${
        picture.picture
      }&token=${getToken()}`;
      return { ...picture, picture: url };
    });

  const sealingSignaturePicturesWithCorrectUrl = sealingSignaturePictures.map(
    (picture) => {
      const url = `${configuration.backendBaseUrl}${
        picture.picture
      }&token=${getToken()}`;
      return { ...picture, picture: url };
    },
  );

  const signatoryDefaultPicture: Picture | undefined =
    signatorySignaturePictures?.find((picture) => picture.isDefault === true);

  const sealingDefaultPicture: Picture | undefined =
    sealingSignaturePictures?.find((picture) => picture.isDefault === true);

  const signature = signatures.find((signatureToTest) => {
    return signatureToTest.identificator === signatureIdentificator;
  });

  let defaultPriorityRule: SignaturePicturePriorityEnum | undefined =
    signature?.signaturePriorityRule;
  if (!defaultPriorityRule) {
    const correctOffice = authStore?.user?.offices.find(
      (office) => office.id === officeId,
    );
    if (correctOffice) {
      if (
        transactionType === "scellement" &&
        correctOffice.scellementPriority
      ) {
        defaultPriorityRule = EnumHelper.stringToEnum(
          SignaturePicturePriorityEnum,
          correctOffice.scellementPriority.toUpperCase(),
        );
      } else if (
        transactionType === "signature" &&
        correctOffice.signaturePriority
      ) {
        defaultPriorityRule = EnumHelper.stringToEnum(
          SignaturePicturePriorityEnum,
          correctOffice.signaturePriority.toUpperCase(),
        );
      }
    }
  }

  const priority: SignaturePicturePriorityEnum | undefined =
    (transactionType === "signature"
      ? signature?.signaturePriorityRule
      : signature?.signaturePriorityRule) ?? defaultPriorityRule;

  const src: string | undefined = SignatureHelper.getCorrectSignaturePicture({
    signatorySignaturePictures:
      transactionType === "signature"
        ? signatorySignaturePicturesWithCorrectUrl
        : sealingSignaturePicturesWithCorrectUrl,
    officeSignatureOrSealingPictures:
      transactionType === "signature"
        ? officeSignaturePictures
        : officeSealingPictures,
    rank: signature?.signaturePictureRank ?? 1,
    isUserInOffice,
    transactionType,
    pattern: signature?.pattern,
    priority,
    image: undefined,
  });

  return {
    signatoryDefaultPicture,
    signatorySignaturePicturesWithCorrectUrl,
    sealingSignaturePicturesWithCorrectUrl,
    officeSignaturePictures,
    officeSealingPictures,
    sealingDefaultPicture,
    sealingDefaultPriorityRule:
      transactionType === "scellement" ? defaultPriorityRule : undefined,
    signature,
    src,
  };
};

export default useSignatoryCardPicture;
